import type {
  TradeOption,
  TradeSelectionMeta,
} from "@simplybusiness/mobius-trade-selector";
import { useEffect, useState } from "react";
import type { AmbiguousTradeModalProps } from "./AmbiguousTradeModal";
import { findAmbiguousTradePair } from "./tradePairs";
import type { AmbiguousTradePair } from "./types";

const GENERAL_CONTRACTOR_POPUP_SELECTION_METHOD = "general_contractor_popup";

/** `TradeSelectionMeta` extended with an optional `selectionMethod` analytics override. */
export type TradeSelectionMetaWithMethod = TradeSelectionMeta & {
  selectionMethod?: string;
};

// `TradeSelectionMeta` satisfies this constraint — `selectionMethod` is optional so existing callers are unaffected.
type CommitTrade<TMeta extends TradeSelectionMetaWithMethod | undefined> = (
  trade: TradeOption,
  meta: TMeta,
) => void;

type UseAmbiguousTradeInterceptorResult<
  TMeta extends TradeSelectionMetaWithMethod | undefined,
> = {
  handleSelectTrade: (trade: TradeOption, meta: TMeta) => void;
  modalProps: AmbiguousTradeModalProps;
};

const withSelectionMethod = (
  meta: TradeSelectionMeta | undefined,
  selectionMethod: string,
): TradeSelectionMetaWithMethod | undefined =>
  meta !== undefined ? { ...meta, selectionMethod } : undefined;

export const useAmbiguousTradeInterceptor = <
  TMeta extends TradeSelectionMetaWithMethod | undefined =
    | TradeSelectionMetaWithMethod
    | undefined,
>(
  commitTrade: CommitTrade<TMeta>,
  onClearTrade?: () => void,
): UseAmbiguousTradeInterceptorResult<TMeta> => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingTrade, setPendingTrade] = useState<{
    trade: TradeOption;
    meta: TMeta;
    tradePair: AmbiguousTradePair;
  } | null>(null);

  // Clear pending trade when modal is dismissed
  useEffect(() => {
    if (!isOpen) {
      setPendingTrade(null);
    }
  }, [isOpen]);

  const handleSelectTrade = (trade: TradeOption, meta: TMeta) => {
    const tradePair = findAmbiguousTradePair(trade.value);
    if (tradePair) {
      // Don't commit yet — wait for the user to confirm via the modal so
      // the primary_detail_selected event only fires once with the final choice.
      setPendingTrade({ trade, meta, tradePair });
      setIsOpen(true);
      return;
    }
    commitTrade(trade, meta);
  };

  const handleConfirmTrade = (confirmedTrade?: string) => {
    if (confirmedTrade && pendingTrade) {
      const meta = withSelectionMethod(
        pendingTrade.meta,
        GENERAL_CONTRACTOR_POPUP_SELECTION_METHOD,
      ) as TMeta;

      if (confirmedTrade === pendingTrade.trade.value) {
        // User confirmed the originally selected trade from the popup.
        commitTrade(pendingTrade.trade, meta);
      } else {
        // User chose the alternative trade from the popup.
        commitTrade(
          {
            value: confirmedTrade,
            label: confirmedTrade,
            vertical: pendingTrade.trade.vertical,
            verticalsWithoutRedirect:
              pendingTrade.trade.verticalsWithoutRedirect,
            definition: pendingTrade.tradePair.alternativeTradeDefinition,
          },
          meta,
        );
      }
    } else {
      // "Select another trade" — clear so the selector reappears.
      onClearTrade?.();
    }
    setIsOpen(false);
  };

  return {
    handleSelectTrade,
    modalProps: {
      isOpen,
      onClose: () => setIsOpen(false),
      confirmTrade: handleConfirmTrade,
      tradePair: pendingTrade?.tradePair,
    },
  };
};
